// Team Lead Certification quiz. Writes only the signed-in agent's own certification record.
import React, { useEffect, useState } from 'react';
import { ref, onValue, update, set } from 'firebase/database';
import { database } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { QUIZ, scoreQuiz, PASS_PERCENT, LESSON_IDS as lessonIds } from '../../data/teamLeadCertQuiz';

const btn = { backgroundColor: '#D4AF37', color: '#000', border: 'none', borderRadius: 6, padding: '10px 24px', cursor: 'pointer', fontWeight: 700, fontSize: 15, fontFamily: 'inherit' };

export default function TeamLeadQuiz() {
  const { user } = useAuth();
  const uid = user?.uid;
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [record, setRecord] = useState(null);
  const [acks, setAcks] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!uid) return undefined;
    const u1 = onValue(ref(database, `users/${uid}/teamLeadCertification`), (s) => setRecord(s.val()));
    const u2 = onValue(ref(database, `users/${uid}/training/teamLeadCert`), (s) => setAcks(s.val() || {}));
    return () => { u1(); u2(); };
  }, [uid]);

  const missingLessons = lessonIds.filter((id) => !acks[id]);
  const allAnswered = QUIZ.every((_, i) => answers[i] !== undefined);

  const submit = async () => {
    const r = scoreQuiz(answers);
    setResult(r);
    setError('');
    const now = new Date().toISOString();
    const attempts = (record?.attempts || 0) + 1;
    const keepPass = record?.passed && !r.passed; // a later failed retake never removes a pass
    const rec = keepPass
      ? { ...record, attempts, lastAttemptAt: now, lastScore: r.score }
      : { passed: r.passed, score: r.score, date: now, attempts, lastAttemptAt: now, lastScore: r.score };
    try {
      await update(ref(database, `users/${uid}`), { teamLeadCertification: rec, teamLeadCertified: !!rec.passed });
      // Mark the quiz step complete in this agent's training progress so the module shows 100% next time
      if (rec.passed) await set(ref(database, `users/${uid}/training/teamLeadCert/tl-quiz`), { completedAt: now });
    } catch (e) {
      setError('Could not save your result. Check your connection and try again.');
    }
  };

  const retake = () => { setAnswers({}); setResult(null); };

  if (missingLessons.length) {
    return <p style={{ fontSize: 17, color: '#9b2c2c' }}>Please read and check "I understand" on every lesson first ({lessonIds.length - missingLessons.length} of {lessonIds.length} done).</p>;
  }

  return (
    <div>
      {record?.passed && !result && (
        <div style={{ background: '#eef7ee', border: '1px solid #9fc79f', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <strong>You're certified!</strong> Score {record.score}% on {new Date(record.date).toLocaleDateString()}. You can retake the quiz to practice; it won't remove your certification.
        </div>
      )}
      {record && !record.passed && !result && record.lastScore !== undefined && (
        <div style={{ background: '#fdf0f0', border: '1px solid #e3a5a5', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          Your last try scored {record.lastScore}% on {new Date(record.lastAttemptAt).toLocaleDateString()} ({record.attempts} {record.attempts === 1 ? 'try' : 'tries'} so far). You need {PASS_PERCENT}% to pass.
        </div>
      )}
      {QUIZ.map((item, i) => {
        const picked = answers[i];
        return (
          <fieldset key={i} style={{ border: '1px solid #e5e5e5', borderRadius: 8, padding: '12px 16px', margin: '0 0 12px' }}>
            <legend style={{ fontWeight: 700, fontSize: 16, padding: '0 6px' }}>{i + 1}. {item.q}</legend>
            {item.options.map((opt, j) => {
              const show = result && (j === item.answer || j === picked);
              const color = !show ? 'inherit' : j === item.answer ? '#2f6f2f' : '#9b2c2c';
              return (
                <label key={j} style={{ display: 'block', fontSize: 15, lineHeight: 1.6, cursor: result ? 'default' : 'pointer', color }}>
                  <input type="radio" name={`q${i}`} disabled={!!result} checked={picked === j} onChange={() => setAnswers({ ...answers, [i]: j })} style={{ marginRight: 8 }} />
                  {opt}
                </label>
              );
            })}
          </fieldset>
        );
      })}
      {error && <div style={{ color: '#9b2c2c', marginBottom: 12 }}>{error}</div>}
      {!result && <button type="button" style={{ ...btn, opacity: allAnswered ? 1 : 0.4 }} disabled={!allAnswered} onClick={submit}>Submit quiz</button>}
      {result && (
        <div style={{ background: result.passed ? '#eef7ee' : '#fdf0f0', border: `1px solid ${result.passed ? '#9fc79f' : '#e3a5a5'}`, borderRadius: 8, padding: '12px 16px' }}>
          <p style={{ fontSize: 17, margin: '0 0 8px' }}>
            {result.passed
              ? `You passed with ${result.score}% (${result.correct} of ${result.total}). Congratulations, you're a Certified Team Lead Agent! Now go make those calls.`
              : `You scored ${result.score}% (${result.correct} of ${result.total}). You need ${PASS_PERCENT}% to pass. Review the lessons and try again. You've got this!`}
          </p>
          <button type="button" style={btn} onClick={retake}>Retake quiz</button>
        </div>
      )}
    </div>
  );
}
